import React, { useState, useEffect } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	IconButton,
	Button,
	Alert,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
	TablePagination,
	Autocomplete,
} from "@mui/material";
import {
	Edit as EditIcon,
	Plus as PlusIcon,
	Search as SearchIcon,
	Check as CheckIcon,
	X as XIcon,
	Download as DownloadIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

const SchoolOwnerships = () => {
	const { supabase } = useAuth();
	const [employeeSchools, setEmployeeSchools] = useState([]);
	const [filteredEmployeeSchools, setFilteredEmployeeSchools] = useState([]);
	const [schools, setSchools] = useState([]);
	const [employees, setEmployees] = useState([]);
	const [districts, setDistricts] = useState([]);
	const [openDialog, setOpenDialog] = useState(false);
	const [successMessage, setSuccessMessage] = useState("");
	const [errorMessage, setErrorMessage] = useState("");
	const [filters, setFilters] = useState({
		search: "",
		visitStatus: "",
		activeStatus: "",
	});
	const [editForm, setEditForm] = useState({
		profile_id: "",
		assignments: [], // Will hold multiple school assignments
	});

	const [currentAssignment, setCurrentAssignment] = useState({
		school_id: "",
		total_students: "",
		visit_status: "Not Visited",
		remarks: "",
		district: "", // New field for district selection
	});

	const [modalError, setModalError] = useState("");
	const [isEditing, setIsEditing] = useState(false);
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(50);
	const [filteredSchools, setFilteredSchools] = useState([]);

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const handleDownload = async () => {
		try {
			// Import XLSX
			const XLSX = await import("xlsx");

			// Prepare data for export
			const exportData = filteredEmployeeSchools.map((assignment) => ({
				"Employee Name": assignment.profiles?.full_name,
				Email: assignment.profiles?.email || "",
				Role: assignment.profiles?.role,
				School: assignment.schools?.name,
				District: assignment.schools?.district || "",
				"Total Students": assignment.total_students || 0,
				"Visit Status": assignment.visit_status,
				Remarks: assignment.remarks || "",
				Status: assignment.active_status ? "Active" : "Inactive",
			}));

			// Create workbook and worksheet
			const ws = XLSX.utils.json_to_sheet(exportData);
			const wb = XLSX.utils.book_new();
			XLSX.utils.book_append_sheet(wb, ws, "School Assignments");

			// Save file
			XLSX.writeFile(wb, "school_assignments.xlsx");
		} catch (error) {
			console.error("Error downloading file:", error);
			setErrorMessage("Error downloading file");
		}
	};

	const handleEdit = (assignment) => {
		setIsEditing(true);
		// Find the district of the school
		const schoolDistrict =
			schools.find((s) => s.id === assignment.school_id)?.district || "";

		setEditForm({
			profile_id: assignment.profile_id,
			assignments: [
				{
					id: assignment.id,
					school_id: assignment.school_id,
					total_students: assignment.total_students,
					visit_status: assignment.visit_status,
					remarks: assignment.remarks || "",
					district: schoolDistrict,
				},
			],
		});
		setOpenDialog(true);

		// Set filtered schools to all schools if editing
		setFilteredSchools(schools);
	};

	const handleUpdate = async () => {
		try {
			setModalError(""); // Clear any previous errors
			const now = new Date().toISOString();

			const allAssignments = currentAssignment.school_id
				? [...editForm.assignments, currentAssignment]
				: editForm.assignments;

			for (const assignment of allAssignments) {
				const { error } = await supabase
					.from("employee_schools")
					.update({
						total_students: assignment.total_students || 0,
						visit_status: assignment.visit_status,
						remarks: assignment.remarks || "",
						updated_at: now,
					})
					.match({
						profile_id: editForm.profile_id,
						school_id: assignment.school_id,
					});

				if (error) throw error;
			}

			setSuccessMessage("Assignment updated successfully");
			setOpenDialog(false);
			setIsEditing(false);
			fetchEmployeeSchools();

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error updating assignment:", err);
			setErrorMessage("Error updating assignment");
		}
	};

	useEffect(() => {
		fetchEmployeeSchools();
		fetchSchools();
		fetchEmployees();
	}, []);

	useEffect(() => {
		let filtered = [...employeeSchools];

		// Filter by search term
		if (filters.search) {
			const searchTerm = filters.search.toLowerCase();
			filtered = filtered.filter(
				(assignment) =>
					assignment.profiles?.full_name?.toLowerCase().includes(searchTerm) ||
					assignment.profiles?.email?.toLowerCase().includes(searchTerm) ||
					assignment.schools?.name?.toLowerCase().includes(searchTerm) ||
					(assignment.schools?.district &&
						assignment.schools.district.toLowerCase().includes(searchTerm))
			);
		}

		// Filter by visit status
		if (filters.visitStatus) {
			filtered = filtered.filter(
				(assignment) => assignment.visit_status === filters.visitStatus
			);
		}

		// Filter by active status
		if (filters.activeStatus) {
			filtered = filtered.filter(
				(assignment) =>
					assignment.active_status === (filters.activeStatus === "true")
			);
		}

		setFilteredEmployeeSchools(filtered);
	}, [filters, employeeSchools]);

	// Extract unique districts from schools
	useEffect(() => {
		if (schools.length > 0) {
			const uniqueDistricts = [
				...new Set(schools.map((school) => school.district).filter(Boolean)),
			];
			setDistricts(uniqueDistricts.sort());

			// Initially set filtered schools to all schools
			setFilteredSchools(schools);
		}
	}, [schools]);

	// Filter schools based on selected district
	useEffect(() => {
		if (currentAssignment.district) {
			const filtered = schools.filter(
				(school) => school.district === currentAssignment.district
			);
			setFilteredSchools(filtered);
		} else {
			setFilteredSchools(schools);
		}
	}, [currentAssignment.district, schools]);

	const fetchEmployees = async () => {
		try {
			const { data, error } = await supabase
				.from("profiles")
				.select("*")
				.in("role", ["ROLE_FUNMATH_ADMIN", "ROLE_FUNMIYO_EMPLOYEE"])
				.eq("status", "Active");

			if (error) throw error;
			setEmployees(data || []);
		} catch (err) {
			console.error("Error fetching employees:", err);
			setErrorMessage("Error loading employees");
		}
	};

	const fetchSchools = async () => {
		try {
			const { data, error } = await supabase
				.from("schools")
				.select("*")
				.eq("status", "Active")
				.order("name");

			if (error) throw error;
			setSchools(data || []);
		} catch (err) {
			console.error("Error fetching schools:", err);
			setErrorMessage("Error loading schools");
		}
	};

	const fetchEmployeeSchools = async () => {
		try {
			const { data, error } = await supabase
				.from("employee_schools")
				.select(
					`
					*,
					profiles:profile_id!inner (
						id,
						full_name,
						email,
						role
					),
					schools:school_id (
						id,
						name,
						district
					)
				`
				)
				.eq("profiles.role", "ROLE_FUNMIYO_EMPLOYEE"); // Filter by role

			if (error) throw error;
			setEmployeeSchools(data || []);
			setFilteredEmployeeSchools(data || []);
		} catch (err) {
			console.error("Error fetching employee schools:", err);
			setErrorMessage("Error loading assignments");
		}
	};

	const handleAddNew = () => {
		setIsEditing(false);
		setEditForm({
			profile_id: "",
			assignments: [],
		});
		setCurrentAssignment({
			school_id: "",
			total_students: "",
			visit_status: "Not Visited",
			remarks: "",
			district: "", // Reset district selection
		});
		setModalError(""); // Clear any modal errors
		setOpenDialog(true);
		setFilteredSchools(schools); // Reset filtered schools to all schools
	};

	const handleSave = async () => {
		try {
			setModalError("");
			const now = new Date().toISOString();

			const allAssignments = currentAssignment.school_id
				? [...editForm.assignments, currentAssignment]
				: editForm.assignments;

			const assignments = allAssignments.map((assignment) => ({
				profile_id: editForm.profile_id,
				school_id: assignment.school_id,
				total_students: assignment.total_students || 0,
				visit_status: assignment.visit_status,
				remarks: assignment.remarks || "",
				active_status: true,
				created_at: now,
				updated_at: now,
			}));

			const { error } = await supabase
				.from("employee_schools")
				.insert(assignments);

			if (error) {
				if (error.code === "23505") {
					setModalError(
						"One or more schools are already assigned to this funmath expert"
					);
					return;
				}
				throw error;
			}

			setSuccessMessage("Assignments saved successfully");
			setOpenDialog(false);
			fetchEmployeeSchools();

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error saving assignments:", err);
			setErrorMessage("Error saving assignments");
		}
	};

	const handleUpdateVisitStatus = async (id, newStatus) => {
		try {
			const { error } = await supabase
				.from("employee_schools")
				.update({
					visit_status: newStatus,
					updated_at: new Date().toISOString(),
				})
				.eq("id", id);

			if (error) throw error;

			setSuccessMessage("Visit status updated successfully");
			fetchEmployeeSchools();

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error updating visit status:", err);
			setErrorMessage("Error updating visit status");
		}
	};

	const handleUpdateActiveStatus = async (id, newStatus) => {
		try {
			const { error } = await supabase
				.from("employee_schools")
				.update({
					active_status: newStatus,
					updated_at: new Date().toISOString(),
				})
				.eq("id", id);

			if (error) throw error;

			setSuccessMessage(
				`Assignment ${newStatus ? "activated" : "deactivated"} successfully`
			);
			fetchEmployeeSchools();

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error updating active status:", err);
			setErrorMessage("Error updating active status");
		}
	};

	// Handler for district selection change
	const handleDistrictChange = (e) => {
		const selectedDistrict = e.target.value;

		// Update current assignment with selected district and reset school selection
		setCurrentAssignment({
			...currentAssignment,
			district: selectedDistrict,
			school_id: "", // Reset school selection when district changes
		});
	};

	return (
		<Box>
			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Typography variant="h6">School Ownerships</Typography>
				<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
					<TextField
						size="small"
						placeholder="Search funmath expert or schools"
						value={filters.search}
						onChange={(e) => setFilters({ ...filters, search: e.target.value })}
						InputProps={{
							startAdornment: <SearchIcon size={20} />,
						}}
					/>
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Visit Status</InputLabel>
						<Select
							value={filters.visitStatus}
							label="Visit Status"
							onChange={(e) =>
								setFilters({ ...filters, visitStatus: e.target.value })
							}>
							<MenuItem value="">All</MenuItem>
							<MenuItem value="Visited">Visited</MenuItem>
							<MenuItem value="Not Visited">Not Visited</MenuItem>
						</Select>
					</FormControl>
					<FormControl size="small" sx={{ minWidth: 160 }}>
						<InputLabel>Status</InputLabel>
						<Select
							value={filters.activeStatus}
							label="Status"
							onChange={(e) =>
								setFilters({ ...filters, activeStatus: e.target.value })
							}>
							<MenuItem value="">All</MenuItem>
							<MenuItem value="true">Active</MenuItem>
							<MenuItem value="false">Inactive</MenuItem>
						</Select>
					</FormControl>
					<Button
						variant="contained"
						startIcon={<DownloadIcon size={20} />}
						onClick={handleDownload}>
						Download
					</Button>
					<Button
						variant="contained"
						startIcon={<PlusIcon size={20} />}
						onClick={handleAddNew}>
						Assign
					</Button>
				</Box>
			</Box>

			<Paper
				elevation={0}
				sx={{
					p: 2,
					border: "1px solid #e5e7eb",
					borderRadius: "8px",
				}}>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Funmath Expert</TableCell>
								<TableCell>Role</TableCell>
								<TableCell>School</TableCell>
								<TableCell>District</TableCell>
								<TableCell>Total Students</TableCell>
								<TableCell>Visit Status</TableCell>
								<TableCell>Remarks</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredEmployeeSchools
								.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
								.map((assignment) => (
									<TableRow key={assignment.id}>
										<TableCell>
											<Box>
												<Typography variant="body2" component="span" display="block">
													{assignment.profiles?.full_name || "—"}
												</Typography>
												{assignment.profiles?.email ? (
													<Typography
														variant="caption"
														color="text.secondary"
														component="span"
														display="block"
														sx={{ wordBreak: "break-all" }}>
														{assignment.profiles.email}
													</Typography>
												) : null}
											</Box>
										</TableCell>
										<TableCell>{assignment.profiles?.role}</TableCell>
										<TableCell>{assignment.schools?.name}</TableCell>
										<TableCell>{assignment.schools?.district || "-"}</TableCell>
										<TableCell>{assignment.total_students || 0}</TableCell>
										<TableCell>
											<Chip
												label={assignment.visit_status}
												color={
													assignment.visit_status === "Visited"
														? "success"
														: "warning"
												}
												size="small"
											/>
										</TableCell>
										<TableCell>{assignment.remarks || "-"}</TableCell>
										<TableCell>
											<Chip
												label={assignment.active_status ? "Active" : "Inactive"}
												color={assignment.active_status ? "success" : "error"}
												size="small"
											/>
										</TableCell>
										<TableCell>
											<IconButton
												size="small"
												color={
													assignment.visit_status === "Not Visited"
														? "success"
														: "warning"
												}
												onClick={() =>
													handleUpdateVisitStatus(
														assignment.id,
														assignment.visit_status === "Not Visited"
															? "Visited"
															: "Not Visited"
													)
												}
												title={`Mark as ${
													assignment.visit_status === "Not Visited"
														? "Visited"
														: "Not Visited"
												}`}>
												{assignment.visit_status === "Not Visited" ? (
													<CheckIcon size={16} />
												) : (
													<XIcon size={16} />
												)}
											</IconButton>
											<IconButton
												size="small"
												color="primary"
												onClick={() => handleEdit(assignment)}
												title="Edit Assignment">
												<EditIcon size={16} />
											</IconButton>
											<IconButton
												size="small"
												color={assignment.active_status ? "error" : "success"}
												onClick={() =>
													handleUpdateActiveStatus(
														assignment.id,
														!assignment.active_status
													)
												}
												title={
													assignment.active_status ? "Deactivate" : "Activate"
												}>
												{assignment.active_status ? (
													<XIcon size={16} />
												) : (
													<CheckIcon size={16} />
												)}
											</IconButton>
										</TableCell>
									</TableRow>
								))}
						</TableBody>
					</Table>
				</TableContainer>
				<Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
					<TablePagination
						component="div"
						count={filteredEmployeeSchools.length}
						page={page}
						onPageChange={handleChangePage}
						rowsPerPage={rowsPerPage}
						onRowsPerPageChange={handleChangeRowsPerPage}
						rowsPerPageOptions={[10, 25, 50, 100]}
					/>
				</Box>
			</Paper>

			<Dialog
				open={openDialog}
				onClose={() => setOpenDialog(false)}
				maxWidth="md"
				fullWidth>
				<DialogTitle>
					{isEditing ? "Edit Assignment" : "Add New Assignment"}
				</DialogTitle>
				<DialogContent>
					{modalError && (
						<Alert severity="error" sx={{ mb: 2 }}>
							{modalError}
						</Alert>
					)}
					<Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
						<Autocomplete
							fullWidth
							size="small"
							disabled={isEditing}
							options={employees}
							value={
								employees.find((e) => e.id === editForm.profile_id) || null
							}
							onChange={(event, newValue) => {
								setEditForm({
									...editForm,
									profile_id: newValue?.id || "",
								});
							}}
							isOptionEqualToValue={(option, value) => option.id === value?.id}
							getOptionLabel={(option) => {
								if (!option) return "";
								const name = option.full_name?.trim() || "";
								const email = option.email?.trim() || "";
								if (name && email) return `${name} · ${email}`;
								return name || email || "";
							}}
							filterOptions={(options, params) => {
								const q = params.inputValue.toLowerCase().trim();
								if (!q) return options;
								return options.filter((emp) => {
									const hay = [
										emp.full_name,
										emp.email,
										emp.role,
									]
										.filter(Boolean)
										.join(" ")
										.toLowerCase();
									return hay.includes(q);
								});
							}}
							renderOption={(props, option) => (
								<li {...props}>
									<Box sx={{ py: 0.25 }}>
										<Typography variant="body2">
											{option.full_name || "—"}
										</Typography>
										{option.email ? (
											<Typography
												variant="caption"
												color="text.secondary"
												display="block"
												sx={{ wordBreak: "break-all" }}>
												{option.email}
											</Typography>
										) : null}
										{option.role ? (
											<Typography variant="caption" color="text.disabled">
												{option.role}
											</Typography>
										) : null}
									</Box>
								</li>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Funmath Expert"
									placeholder="Search by name, email, or role"
								/>
							)}
						/>

						<TableContainer>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell width="15%">District</TableCell>
										<TableCell width="20%">School</TableCell>
										<TableCell width="15%">Total Students</TableCell>
										<TableCell width="15%">Visit Status</TableCell>
										<TableCell width="25%">Remarks</TableCell>
										<TableCell width="10%">Action</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{isEditing ? (
										// Edit mode - show existing assignment
										editForm.assignments.map((assignment, index) => (
											<TableRow key={index}>
												<TableCell>
													{assignment.district ||
														schools.find((s) => s.id === assignment.school_id)
															?.district ||
														"-"}
												</TableCell>
												<TableCell>
													{
														schools.find((s) => s.id === assignment.school_id)
															?.name
													}
												</TableCell>
												<TableCell>
													<TextField
														size="small"
														type="number"
														fullWidth
														value={assignment.total_students}
														onChange={(e) => {
															const newAssignments = [...editForm.assignments];
															newAssignments[index].total_students =
																e.target.value;
															setEditForm({
																...editForm,
																assignments: newAssignments,
															});
														}}
													/>
												</TableCell>
												<TableCell>
													<FormControl size="small" fullWidth>
														<Select
															value={assignment.visit_status}
															onChange={(e) => {
																const newAssignments = [
																	...editForm.assignments,
																];
																newAssignments[index].visit_status =
																	e.target.value;
																setEditForm({
																	...editForm,
																	assignments: newAssignments,
																});
															}}>
															<MenuItem value="Visited">Visited</MenuItem>
															<MenuItem value="Not Visited">
																Not Visited
															</MenuItem>
														</Select>
													</FormControl>
												</TableCell>
												<TableCell>
													<TextField
														size="small"
														fullWidth
														value={assignment.remarks || ""}
														onChange={(e) => {
															const newAssignments = [...editForm.assignments];
															newAssignments[index].remarks = e.target.value;
															setEditForm({
																...editForm,
																assignments: newAssignments,
															});
														}}
													/>
												</TableCell>
												<TableCell>
													{/* No action needed in edit mode */}
												</TableCell>
											</TableRow>
										))
									) : (
										// Add mode - show existing assignments and input row
										<>
											{editForm.assignments.map((assignment, index) => {
												const school = schools.find(
													(s) => s.id === assignment.school_id
												);
												return (
													<TableRow key={index}>
														<TableCell>{school?.district || "-"}</TableCell>
														<TableCell>{school?.name}</TableCell>
														<TableCell>{assignment.total_students}</TableCell>
														<TableCell>{assignment.visit_status}</TableCell>
														<TableCell>{assignment.remarks || "-"}</TableCell>
														<TableCell>
															<IconButton
																size="small"
																color="error"
																onClick={() => {
																	const newAssignments =
																		editForm.assignments.filter(
																			(_, idx) => idx !== index
																		);
																	setEditForm({
																		...editForm,
																		assignments: newAssignments,
																	});
																}}>
																<XIcon size={16} />
															</IconButton>
														</TableCell>
													</TableRow>
												);
											})}
											<TableRow>
												<TableCell>
													<FormControl fullWidth size="small">
														<Select
															value={currentAssignment.district}
															displayEmpty
															onChange={handleDistrictChange}>
															<MenuItem value="">All Districts</MenuItem>
															{districts.map((district) => (
																<MenuItem key={district} value={district}>
																	{district}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</TableCell>
												<TableCell>
													<FormControl fullWidth size="small">
														<Select
															value={currentAssignment.school_id}
															displayEmpty
															onChange={(e) =>
																setCurrentAssignment({
																	...currentAssignment,
																	school_id: e.target.value,
																})
															}>
															<MenuItem value="">Select School</MenuItem>
															{filteredSchools
																.filter(
																	(school) =>
																		!editForm.assignments.some(
																			(a) => a.school_id === school.id
																		)
																)
																.map((school) => (
																	<MenuItem key={school.id} value={school.id}>
																		{school.name}
																	</MenuItem>
																))}
														</Select>
													</FormControl>
												</TableCell>
												<TableCell>
													<TextField
														size="small"
														type="number"
														fullWidth
														value={currentAssignment.total_students}
														onChange={(e) =>
															setCurrentAssignment({
																...currentAssignment,
																total_students: e.target.value,
															})
														}
													/>
												</TableCell>
												<TableCell>
													<FormControl size="small" fullWidth>
														<Select
															value={currentAssignment.visit_status}
															onChange={(e) =>
																setCurrentAssignment({
																	...currentAssignment,
																	visit_status: e.target.value,
																})
															}>
															<MenuItem value="Visited">Visited</MenuItem>
															<MenuItem value="Not Visited">
																Not Visited
															</MenuItem>
														</Select>
													</FormControl>
												</TableCell>
												<TableCell>
													<TextField
														size="small"
														fullWidth
														value={currentAssignment.remarks || ""}
														onChange={(e) =>
															setCurrentAssignment({
																...currentAssignment,
																remarks: e.target.value,
															})
														}
													/>
												</TableCell>
												<TableCell>
													<IconButton
														size="small"
														color="primary"
														onClick={() => {
															if (currentAssignment.school_id) {
																setEditForm({
																	...editForm,
																	assignments: [
																		...editForm.assignments,
																		currentAssignment,
																	],
																});
																setCurrentAssignment({
																	school_id: "",
																	total_students: "",
																	visit_status: "Not Visited",
																	remarks: "",
																	district: currentAssignment.district, // Preserve district selection
																});
															}
														}}
														disabled={!currentAssignment.school_id}>
														<PlusIcon size={16} />
													</IconButton>
												</TableCell>
											</TableRow>
										</>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenDialog(false)}>Cancel</Button>
					<Button
						onClick={isEditing ? handleUpdate : handleSave}
						variant="contained"
						disabled={
							!editForm.profile_id ||
							(editForm.assignments.length === 0 &&
								!currentAssignment.school_id)
						}>
						{isEditing ? "Update" : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default SchoolOwnerships;
